import React, { useState, useEffect } from 'react';
import { CheckCircle2, Loader2, Stethoscope } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { getUser } from '../utils/auth';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'AE', name: 'United Arab Emirates' }, { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' }, { code: 'KW', name: 'Kuwait' }, { code: 'OM', name: 'Oman' },
  { code: 'BH', name: 'Bahrain' }, { code: 'IQ', name: 'Iraq' }, { code: 'YE', name: 'Yemen' },
  { code: 'BD', name: 'Bangladesh' }, { code: 'PK', name: 'Pakistan' }, { code: 'NP', name: 'Nepal' },
  { code: 'LK', name: 'Sri Lanka' }, { code: 'MV', name: 'Maldives' }, { code: 'MM', name: 'Myanmar' },
  { code: 'AF', name: 'Afghanistan' }, { code: 'UZ', name: 'Uzbekistan' },
  { code: 'NG', name: 'Nigeria' }, { code: 'KE', name: 'Kenya' }, { code: 'ET', name: 'Ethiopia' },
  { code: 'GH', name: 'Ghana' }, { code: 'TZ', name: 'Tanzania' }, { code: 'UG', name: 'Uganda' },
  { code: 'SD', name: 'Sudan' }, { code: 'ZM', name: 'Zambia' }, { code: 'OTHER', name: 'Other' },
];

export default function ConsultationRequestDialog({ open, onOpenChange, context }) {
  const { type = 'treatment', name = '', treatmentCode = 'other' } = context || {};
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', country: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      const u = getUser();
      if (u && u.role === 'patient') {
        setForm((f) => ({ ...f, full_name: u.full_name || u.name || '', email: u.email || '' }));
      }
    }
  }, [open]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const contextLabel = {
    treatment: `Treatment: ${name}`,
    doctor: `Doctor: ${name}`,
    hospital: `Hospital: ${name}`,
  }[type];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.country) {
      toast.error('Please fill in your name, email, phone and country.');
      return;
    }
    setSubmitting(true);
    try {
      const notes = `Consultation request from ${type} page — ${contextLabel}.` +
        (form.message ? `\n\nPatient message: ${form.message}` : '');
      const res = await fetch(`${API_BASE}/api/public/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          country: form.country,
          treatment: treatmentCode || 'other',
          medical_condition: '',
          additional_notes: notes,
          preferred_contact: 'whatsapp',
          consent: true,
          source: `${type}_page`,
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setDone(true);
      toast.success('Consultation request sent! Our care team will reach out shortly.');
    } catch (err) {
      toast.error('Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="consultation-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="consultation-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900">Request received</h3>
            <p className="text-gray-600 mt-2 text-sm">
              Thanks {form.full_name.split(' ')[0]}! Our medical coordinators will contact you within 24 hours to plan your care.
            </p>
            <Button className="mt-6 w-full" onClick={() => onOpenChange(false)} data-testid="consultation-close-btn">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
                <Stethoscope className="w-5 h-5" />
                <DialogTitle>Request a Consultation</DialogTitle>
              </div>
              <DialogDescription>
                {contextLabel} — share your details and our care team will get back to you.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="cr-name">Full name *</Label>
                <Input id="cr-name" value={form.full_name} onChange={update('full_name')} placeholder="Your name" data-testid="consultation-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cr-email">Email *</Label>
                  <Input id="cr-email" type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" data-testid="consultation-email" />
                </div>
                <div>
                  <Label htmlFor="cr-phone">Phone *</Label>
                  <Input id="cr-phone" value={form.phone} onChange={update('phone')} placeholder="+00 000 000 0000" data-testid="consultation-phone" />
                </div>
              </div>
              <div>
                <Label>Country *</Label>
                <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                  <SelectTrigger data-testid="consultation-country"><SelectValue placeholder="Select your country" /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cr-msg">Message (optional)</Label>
                <Textarea id="cr-msg" value={form.message} onChange={update('message')} placeholder="Tell us about your condition or questions..." rows={3} data-testid="consultation-message" />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="consultation-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Send Request'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
